import { AxiosError } from 'axios';
import { URL_ENTE_DESCENTRALIZADO, VersaoContratoEnum } from 'models/contratos';
import { CreateRouteParams, EditRouteParams } from 'pages/Contratos/Fluxos/FluxoDescentralizados/__types';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import { RootState } from 'store';
import { contratoAlteraStatus, deleteDocumentoAditivo, deleteDocumentoContrato, deleteDocumentoDistrato, loadContratoAdendos } from 'store/modules/AllContratos/AllContratosActions';
import SnackbarUtils from 'utils/SnackbarUtils';

export const useAccordionMenuItem = (
    id: number | null,
    versaoContrato: number) => {
    const adendosAll = useSelector(
        (state: RootState) => state.allContratos.adendosAll
    );
    const languageValues = useSelector(
        (states: RootState) => states.languages.languageValues
    );
    const params = useParams<CreateRouteParams & EditRouteParams>();
    const location = useLocation()
    const dispatch = useDispatch();

    const fluxoAssessor = location.pathname.includes(URL_ENTE_DESCENTRALIZADO.ASSESSOR);
    const fluxoEntePublico = location.pathname.includes(URL_ENTE_DESCENTRALIZADO.ENTE_PUBLICO);
    const fluxoEntePrivado = location.pathname.includes(URL_ENTE_DESCENTRALIZADO.ENTE_PRIVADO);

    const inibeBotaoExcluir = () => {
        return adendosAll.some(adendo => adendo.id === id && adendo.permiteExclusao === true);
    };

    const notifyUser = (successMessage: string) => {
        return (status: boolean) => {
            if (status) {
                SnackbarUtils.enqueueSnackbar({
                    message: successMessage,
                    options: { variant: 'success' },
                });
            }
        };
    };

    const sucessoExclusao = (idDocument: number, successMessage: string) => {
        notifyUser(successMessage);
        if (fluxoAssessor || fluxoEntePublico || fluxoEntePrivado) {
            dispatch(loadContratoAdendos(Number(params.id)));
        }
    }

    const errorExclusao = (error: AxiosError | string, errorMessage: string) => {
        let finalErrorMessage: string;

        if (typeof error === 'object' && error !== null) {
            finalErrorMessage = error.response?.statusText || errorMessage;
        } else {
            finalErrorMessage = errorMessage;
        }

        notifyUser(finalErrorMessage);
    };

    const deleteDocument = (
        idDocument: number,
        versaoContratoDocument: number,
        deleteAction: any
    ) => {
        dispatch(
            deleteAction(
                id,
                () =>
                    sucessoExclusao(idDocument, languageValues.successDeleteDocumento),
                (error: AxiosError | string) =>
                    errorExclusao(error, languageValues.errorDeleteDocumento)
            )
        );
    };

    const excluiDocumento = async () => {
        if (!id) return;

        switch (versaoContrato) {
            case VersaoContratoEnum.CONTRATO:
                deleteDocument(id, versaoContrato, deleteDocumentoContrato);
                break;
            case VersaoContratoEnum.ADITIVO:
                deleteDocument(id, versaoContrato, deleteDocumentoAditivo);
                break;
            case VersaoContratoEnum.DISTRATO:
                deleteDocument(id, versaoContrato, deleteDocumentoDistrato);
                break;
            default:
                notifyUser(languageValues.errorDeleteDocumento);
        }
    };

    const handleSuccessResponseAlteraStatus = (
        idDocument: number,
        handleResponse: (status: boolean) => void
    ) => {
        handleResponse(true);
        if (fluxoAssessor || fluxoEntePublico || fluxoEntePrivado) {
            dispatch(loadContratoAdendos(Number(params.id)));
        }
    };

    const handleErrorResponseStatus = (
        error: AxiosError | string,
        handleResponse: (status: boolean) => void
    ) => {
        let errorMessage: string | undefined;
        if (typeof error === 'object' && error !== null) {
            errorMessage = error.response?.statusText;
        }
        handleResponse(false);
        SnackbarUtils.enqueueSnackbar({
            message: errorMessage,
            options: { variant: 'error' },
        });
    };

    const onError = (
        error: AxiosError | string,
        handleResponse: (status: boolean) => void
    ) => {
        handleErrorResponseStatus(error, handleResponse);
    };

    const contractDisabled = () => {
        const handleResponse = notifyUser(languageValues.successDesativarDocumento);

        const onSuccess = () =>
            handleSuccessResponseAlteraStatus(Number(params.id), handleResponse);

        const actions: { [key: number]: () => void } = {
            [VersaoContratoEnum.CONTRATO]: () => {
                dispatch(
                    contratoAlteraStatus(
                        id,
                        VersaoContratoEnum.CONTRATO,
                        onSuccess,
                        (error) => onError(error, handleResponse)
                    )
                );
            },
            [VersaoContratoEnum.ADITIVO]: () => {
                dispatch(
                    contratoAlteraStatus(
                        id,
                        VersaoContratoEnum.ADITIVO,
                        onSuccess,
                        (error) => onError(error, handleResponse)
                    )
                );
            },
            [VersaoContratoEnum.DISTRATO]: () => {
                dispatch(
                    contratoAlteraStatus(
                        id,
                        VersaoContratoEnum.DISTRATO,
                        onSuccess,
                        (error) => onError(error, handleResponse)
                    )
                );
            },
        };

        if (actions[versaoContrato]) {
            actions[versaoContrato]();
        } else {
            SnackbarUtils.enqueueSnackbar({
                message: languageValues.errorVersaoContrato,
                options: { variant: 'error' },
            });
        }
    };

    const contractEnabled = () => {
        const handleResponse = notifyUser(languageValues.successAtivarDocumento);

        const onSuccess = () =>
            handleSuccessResponseAlteraStatus(Number(params.id), handleResponse);

        const actions: { [key: number]: () => void } = {
            [VersaoContratoEnum.CONTRATO]: () => {
                dispatch(
                    contratoAlteraStatus(
                        id,
                        VersaoContratoEnum.CONTRATO,
                        onSuccess,
                        (error) => onError(error, handleResponse)
                    )
                );
            },
            [VersaoContratoEnum.ADITIVO]: () => {
                dispatch(
                    contratoAlteraStatus(
                        id,
                        VersaoContratoEnum.ADITIVO,
                        onSuccess,
                        (error) => onError(error, handleResponse)
                    )
                );
            },
            [VersaoContratoEnum.DISTRATO]: () => {
                dispatch(
                    contratoAlteraStatus(
                        id,
                        VersaoContratoEnum.DISTRATO,
                        onSuccess,
                        (error) => onError(error, handleResponse)
                    )
                );
            },
        };

        if (actions[versaoContrato]) {
            actions[versaoContrato]();
        } else {
            SnackbarUtils.enqueueSnackbar({
                message: languageValues.errorVersaoContrato,
                options: { variant: 'error' },
            });
        }
    };

    return {
        contractEnabled,
        contractDisabled,
        excluiDocumento,
        deleteDocument,
        inibeBotaoExcluir
    }
}

